import React from "react";
import {
  Box, Card, CardContent, Grid, Stack, Typography,
  LinearProgress, TextField, FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";

import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend
} from "recharts";

import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";

import { getRequest } from "Includes/api/apicall";
import { GET_URL } from "Includes/urls";

const COLORS = ["#22c55e", "#3b82f6", "#a855f7", "#f59e0b", "#ef4444"];

export default function FinanceDashboard(props) {
  const [loading, setLoading] = React.useState(false);
  const [fromDate, setFromDate] = React.useState("");
  const [toDate, setToDate] = React.useState("");
  const [academicYears, setAcademicYears] = React.useState([]);
  const [academicYearId, setAcademicYearId] = React.useState("");

  const [cards, setCards] = React.useState({
    income: 0,
    expense: 0,
    net: 0,
  });

  const [paymentData, setPaymentData] = React.useState([]);
  const [expenseData, setExpenseData] = React.useState([]);
  const [monthlyData, setMonthlyData] = React.useState([]);

  React.useEffect(() => {
    getRequest(GET_URL.academicyear.api, { limit: 200, pageno: 1 }, props)
      .then((res) => {
        const list = res?.data?.data || [];
        if (!Array.isArray(list) || !list.length) return;
        setAcademicYears(list);
        setAcademicYearId((prev) => {
          if (prev) return prev;
          const sorted = [...list].sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0));
          return sorted[0] && sorted[0].id != null ? String(sorted[0].id) : "";
        });
      })
      .catch(() => {});
  }, [props]);

  const loadData = React.useCallback(() => {
    setLoading(true);

    const params = {};
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    if (academicYearId) params.academic_year_id = academicYearId;

    getRequest(GET_URL.finance_dashboard.api, params, props)
      .then((dashboardRes) => {
        const dashboardData = dashboardRes?.data?.data || {};

        setCards(dashboardData.cards || { income: 0, expense: 0, net: 0 });
        setPaymentData(dashboardData.paymentData || []);
        setExpenseData(dashboardData.expenseData || []);
        setMonthlyData(dashboardData.monthlyData || []);
      })
      .finally(() => setLoading(false));
  }, [academicYearId, fromDate, props, toDate]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const StatCard = ({ title, value, color, icon }) => (
    <Card sx={{
      borderRadius: 3,
      background: "linear-gradient(135deg, #0f172a, #1e293b)",
      color: "#fff"
    }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between">
          <Box>
            <Typography variant="caption">{title}</Typography>
            <Typography variant="h5" fontWeight={700}>
              ₹ {value.toFixed(0)}
            </Typography>
          </Box>
          {icon}
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Box p={3}>
      <Typography variant="h5" fontWeight={700}>
        Finance Dashboard
      </Typography>

      {/* Filters */}
      <Grid container spacing={2} mt={1} mb={2}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel id="finance-dashboard-year-label">Academic year</InputLabel>
            <Select
              labelId="finance-dashboard-year-label"
              label="Academic year"
              value={academicYearId}
              onChange={(e) => setAcademicYearId(e.target.value)}
            >
              <MenuItem value="">
                <em>All years</em>
              </MenuItem>
              {academicYears.map((y) => (
                <MenuItem key={y.id} value={String(y.id)}>
                  {y.name || y.code || `Year #${y.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            type="date"
            fullWidth
            size="small"
            label="From date"
            InputLabelProps={{ shrink: true }}
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            type="date"
            fullWidth
            size="small"
            label="To date"
            InputLabelProps={{ shrink: true }}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </Grid>
      </Grid>

      {loading && <LinearProgress />}

      {/* Stats */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={4}>
          <StatCard
            title="Income"
            value={cards.income}
            icon={<TrendingUpIcon />}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard
            title="Expense"
            value={cards.expense}
            icon={<TrendingDownIcon />}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard
            title="Net"
            value={cards.net}
            icon={<AccountBalanceWalletIcon />}
          />
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={2}>
        {/* Expense Pie */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography fontWeight={600}>Expense By Type</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={expenseData} dataKey="value" outerRadius={100}>
                    {expenseData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Payment Pie */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography fontWeight={600}>Payment Methods</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={paymentData} dataKey="value" outerRadius={100}>
                    {paymentData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Bar Chart */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography fontWeight={600}>Monthly Overview</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="income" fill="#22c55e" />
                  <Bar dataKey="expense" fill="#ef4444" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
